<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col">
        <div
          class="row"
          v-if="isAddOrEdit"
        >
          <div class="col-lg-8 offset-lg-2">
            <form @submit.prevent="saveGame(game)">
              <div class="card mt-0">
                <div class="card-header">
                  <span v-if="game.id">
                    {{ $t('gpool.edit_game') }}: {{ game.name }}
                  </span>
                  <span v-else>{{ $t('gpool.create_game') }}</span>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <div class="row">
                      <div class="col">
                        <label
                          class="form-label"
                          for="name"
                        >
                          {{ $t('gpool.form_name') }}
                        </label>
                        <input
                          class="form-control"
                          type="text"
                          name="name"
                          id="name"
                          :disabled="posting"
                          v-model="game.name"
                          required
                        />
                      </div>
                      <div class="col">
                        <label
                          for="logo"
                          class="form-label"
                        >
                          {{ $t('gpool.form_logo') }}
                        </label>
                        <input
                          type="file"
                          @change="onFileChange"
                          name="logo"
                          id="logo"
                          :disabled="posting"
                          class="form-control"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="mb-3">
                    <div class="row">
                      <div class="col">
                        <label
                          class="form-label"
                          for="category_id"
                        >
                          {{ $t('gpool.form_category') }}
                        </label>
                        <select
                          name="category_id"
                          id="category_id"
                          class="form-select"
                          v-model="game.category_id"
                          required
                        >
                          <option
                            value=""
                            selected
                            disabled
                          >
                            {{ $t('gpool.form_select_category') }}
                          </option>
                          <option
                            v-for="category in categories"
                            :key="category.id"
                            :value="category.id"
                          >
                            {{ category.name }}
                          </option>
                        </select>
                      </div>
                      <div class="col">
                        <label
                          class="form-label"
                          for="company_id"
                        >
                          {{ $t('gpool.form_company') }}
                        </label>
                        <select
                          name="company_id"
                          id="company_id"
                          class="form-select"
                          :disabled="!game.category_id"
                          v-model="game.company_id"
                          required
                        >
                          <option
                            value=""
                            selected
                            disabled
                          >
                            {{ $t('gpool.form_select_company') }}
                          </option>
                          <option
                            v-for="company in filteredCompanies"
                            :key="company.id"
                            :value="company.id"
                          >
                            {{ company.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="mb-3">
                    <div class="row">
                      <div class="col">
                        <label
                          class="form-label"
                          for="jsoneditor"
                        >
                          {{ $t('gpool.form_extras') }}
                        </label>
                        <div
                          id="jsoneditor"
                          style="height: 300px"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-footer">
                  <button
                    :disabled="posting"
                    type="submit"
                    class="btn"
                    :class="{
                      'btn-success': !game.id,
                      'btn-primary': game.id
                    }"
                  >
                    <i
                      v-if="posting"
                      class="fa fa-spinner fa-spin"
                    ></i>
                    <template v-else>
                      <span v-if="game.id">
                        {{ $t('gpool.form_update') }}
                      </span>
                      <span v-else>
                        {{ $t('gpool.form_add') }}
                      </span>
                    </template>
                  </button>
                  <button
                    class="btn btn-link"
                    @click="setIsAddOrEdit(false)"
                  >
                    {{ $t('gpool.form_cancel') }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div
          class="position-relative card mt-0"
          v-else
        >
          <div class="card-header">
            <div class="align-items-center row">
              <div class="col-6">
                <div class="mb-0 card-title h5">
                  {{ $t('gpool.menu_games') }}
                </div>
              </div>
              <div class="text-end col-6">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="setIsAddOrEdit(true)"
                >
                  <i class="fa fa-plus"></i>
                  <span class="ms-2 align-middle">{{
                    $t('gpool.create_game')
                  }}</span>
                </button>
              </div>
            </div>
          </div>
          <div
            class="rounded-0 position-absolute w-100 progress"
            style="height: 6px"
            v-if="loading"
          >
            <div
              role="progressbar"
              class="progress-bar progress-bar-animated progress-bar-striped"
              aria-valuenow="100"
              aria-valuemin="0"
              aria-valuemax="100"
              style="width: 100%"
            ></div>
          </div>
          <div
            v-else-if="!games.length"
            class="text-center p-4"
          >
            {{ $t('gpool.no_data') }}
          </div>
          <AgGridVue
            style="height: 500px; padding: 15px"
            class="ag-theme-alpine"
            :columnDefs="columnDefs"
            :rowData="rowData"
            @gridReady="gridReady"
            :gridOptions="gridOptions"
            :pagination="true"
            v-show="!loading && games.length"
          >
          </AgGridVue>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import 'ag-grid-community/dist/styles/ag-grid.css'
import 'ag-grid-community/dist/styles/ag-theme-alpine.css'
import { AgGridVue } from 'ag-grid-vue'

export default {
  name: 'Games',
  components: {
    AgGridVue
  },
  watch: {
    async games() {
      this.gridApi.setRowData(this.games)
    },
    isAddOrEdit() {
      var self = this
      setTimeout(() => {
        self.initJsonEditor()
      }, 1)
    }
  },
  computed: {
    columnDefs() {
      var self = this
      return [
        {
          headerName: 'ID',
          field: 'id',
          width: 50,
          flex: 1,
          sortable: true
        },
        // {
        //   headerName: this.$t('gpool.table_logo'),
        //   field: 'logo_url',
        //   width: 100,
        //   cellRenderer: params => {
        //     return params.value
        //       ? `<img style="padding:5px;max-width: 90px; max-height: 45px" src="${params.value}" />`
        //       : ''
        //   }
        // },
        {
          headerName: this.$t('gpool.table_name'),
          field: 'name',
          flex: 2,
          filter: true
        },
        {
          headerName: this.$t('gpool.table_category'),
          field: 'category_id',
          cellRenderer: params => {
            return this.getCategoryName(params.value)
          },
          cellStyle: { textAlign: 'center' },
          filter: true,
          filterParams: {
            filterOptions: ['contains'],
            clearButton: true,
            textCustomComparator: (type, id, text) => {
              const original = this.getCategoryName(+id)
              return original.toLowerCase().includes(text.toLowerCase())
            }
          }
        },
        {
          headerName: this.$t('gpool.table_company'),
          field: 'company_id',
          cellRenderer: params => {
            return this.getCompanyName(params.value)
          },
          cellStyle: { textAlign: 'center' },
          filter: 'agTextColumnFilter',
          filterParams: {
            filterOptions: ['contains'],
            clearButton: true,
            textCustomComparator: (type, id, text) => {
              const original = this.getCompanyName(+id)
              return original.toLowerCase().includes(text.toLowerCase())
            }
          }
        },
        {
          headerName: this.$t('gpool.table_order'),
          field: 'order',
          flex: 1,
          editable: true,
          cellStyle: { backgroundColor: '#eee', textAlign: 'center' },
          cellRenderer: params => {
            return params.value
          },
          onCellValueChanged: async params => {
            await self.updateOrder(params.data)
          },
          sortable: true
        },
        // {
        //   headerName: this.$t("gpool.table_order"),
        //   field: "order",
        //   width: 75,
        //   cellRenderer: params => {
        //     return `<input style="margin-top:6px;" type="text" class="form-control text-center form-control-sm" value="${params.value}" />`;
        //   },
        //   cellStyle: { textAlign: "center" },
        //   onCellBlur: async params => {
        //     await self.updateOrder(params.data);
        //     this.gridApi.applyTransaction({ update: [params.data] });
        //     this.gridApi.refreshCells();
        //   }
        // },
        {
          headerName: this.$t('gpool.table_status'),
          field: 'status',
          flex: 1,
          cellRenderer: params => {
            return `<div class="form-switch"><input type="checkbox" class="form-check-input" id="status" style="pointer-events: none" ${
              params.value ? 'checked' : ''
            } /></div>`
          },
          onCellClicked: async params => {
            await self.toggleStatus(params.data)
            params.data.status = !params.data.status
            this.gridApi.applyTransaction({ update: [params.data] })
            this.gridApi.refreshCells()
          },
          sortable: true
        },

        {
          headerName: '',
          field: 'id',
          cellRenderer: () => {
            return `<div role="group" class="btn-group"><button type="button" class="btn"><i class="fa fa-pencil"></i></button>`
          },
          cellStyle: { textAlign: 'center' },
          onCellClicked: async params => {
            await self.editGame(params.data.id)
          },
          width: 50
        },
        {
          headerName: '',
          field: 'id',
          cellRenderer: () => {
            return `<div role="group" class="btn-group"><button type="button" class="btn"><i class="fa fa-trash"></i></button>`
          },
          cellStyle: { textAlign: 'center' },
          onCellClicked: async params => {
            await self.deleteGame(params.data.id)
          },
          width: 50
        }
      ]
    },
    filteredCompanies() {
      return this.companies.filter(
        comp => comp.category_id === this.game.category_id
      )
    },
    ...mapGetters('category', ['categories']),
    ...mapGetters('company', ['companies']),
    ...mapGetters('game', [
      'games',
      'game',
      'loading',
      'posting',
      'isAddOrEdit',
      'statusing'
    ])
  },
  beforeDestroy() {
    this.setLoading(true)
    this.setIsAddOrEdit(false)
  },
  mounted() {},
  methods: {
    initJsonEditor() {
      var self = this
      const container = document.getElementById('jsoneditor')
      console.log('container', container)
      const options = {
        mode: 'code',
        mainMenuBar: false,
        navigationBar: false,
        statusBar: false,
        search: false,
        indentation: 2,
        enableSort: false,
        enableTransform: false,
        enableStandalone: false,
        onChange: function () {
          self.game.extras = editor.get()
        }
      }
      const editor = new JSONEditor(container, options)

      // set json
      const initialJson = this.game.extras
      editor.set(initialJson)

      // get json
      const updatedJson = editor.get()
    },
    async gridReady(event) {
      this.gridApi = event.api
      this.gridApi.showLoadingOverlay()
      await this.getCategories({ bypass: true })
      await this.getCompanies({ bypass: true })
      await this.getGames()
      this.gridApi.setRowData(this.games)
      this.gridApi.hideOverlay()
    },
    onFileChange(e) {
      const file = e.target.files[0]
      this.game.logo = file
    },
    getCategoryName(id) {
      const category = this.categories.find(category => category.id === id)
      return category ? category.name : ''
    },
    getCompanyName(id) {
      const company = this.companies.find(company => company.id === id)
      return company ? company.name : ''
    },
    ...mapActions('game', [
      'getGames',
      'saveGame',
      'setIsAddOrEdit',
      'toggleStatus',
      'editGame',
      'deleteGame',
      'updateOrder',
      'setLoading'
    ]),
    ...mapActions('category', ['getCategories']),
    ...mapActions('company', ['getCompanies'])
  },
  data() {
    return {
      rowData: [],
      gridApi: null,
      gridOptions: {}
    }
  }
}
</script>
<style>
.jsoneditor-poweredBy {
  display: none !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/Games.vue